import type { Infer } from 'superstruct';

import type { FacebookSavedAudienceTargetingGeoLocations } from '@adflow/shared/data-facebook-ads';
import type {
	AddTargetingGeoLocationCountry as AddGoogleAdsTargetingGeoLocationCountryStruct,
	AddTargetingGeoLocationCustomLocation as AddGoogleAdsTargetingGeoLocationCustomLocationStruct,
	AddTargetingGeoLocationZip as AddGoogleAdsTargetingGeoLocationZipStruct,
	ViewGoogleAdsCampaignLocations,
} from '@adflow/shared/data-google-ads';
import type { SalesforceLocationData } from '@adflow/shared/data-salesforce-import';

import { determineZipCodeType } from '../../shared/locations';

const TRAILING_TIMESTAMP_REGEX =
	/(?:\s*-\s*)?\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}\s*$/u;

export function stripTrailingTimestamp(name: string): string {
	return name.replace(TRAILING_TIMESTAMP_REGEX, '').trimEnd();
}

interface GoogleCustomLocation {
	name: string;
	address_string: string;
	radius: number;
	distance_unit: string;
}

interface GoogleZipLocation {
	key: string;
	country_code: string;
}

interface GoogleCountryLocation {
	key: string;
}

// Convert facebook saved audiences to google locations
export function convertFacebookGeoLocationsToGoogleLocations(
	name: string,
	geoLocations: FacebookSavedAudienceTargetingGeoLocations
): (GoogleCustomLocation | GoogleZipLocation | GoogleCountryLocation)[] {
	const googleLocations: (
		GoogleCustomLocation | GoogleZipLocation | GoogleCountryLocation
	)[] = [];

	if (geoLocations.countries) {
		for (const countryCode of geoLocations.countries) {
			googleLocations.push({
				key: countryCode,
			});
		}
	}

	if (geoLocations.cities) {
		for (const city of geoLocations.cities) {
			const address = `${city.name}, ${city.region}, ${city.country}`;
			googleLocations.push({
				name: `${name} - City`,
				address_string: address,
				radius: city.radius || 25,
				distance_unit: city.distance_unit || 'mile',
			});
		}
	}

	if (geoLocations.zips) {
		for (const zip of geoLocations.zips) {
			googleLocations.push({
				key: `${zip.name}`,
				country_code: zip.country,
			});
		}
	}

	if (geoLocations.custom_locations) {
		for (const location of geoLocations.custom_locations) {
			googleLocations.push({
				name: `${name}`,
				address_string: location.address_string,
				radius: location.radius || 25,
				distance_unit: location.distance_unit || 'mile',
			});
		}
	}

	if (geoLocations.places) {
		for (const place of geoLocations.places) {
			googleLocations.push({
				name: `${name} - ${place.name}`,
				address_string: `${place.latitude}, ${place.longitude}`,
				radius: place.radius || 25,
				distance_unit: place.distance_unit || 'mile',
			});
		}
	}

	if (geoLocations.subcities) {
		for (const subcity of geoLocations.subcities) {
			const address = `${subcity.name}, ${subcity.region}, ${subcity.country}`;
			googleLocations.push({
				name: `${name} - Subcity`,
				address_string: address,
				radius: 25, // Default radius for subcities
				distance_unit: 'mile',
			});
		}
	}

	return googleLocations;
}

type AddGoogleAdsTargetingGeoLocationCustomLocation = Infer<
	typeof AddGoogleAdsTargetingGeoLocationCustomLocationStruct
>;
type AddGoogleAdsTargetingGeoLocationZip = Infer<
	typeof AddGoogleAdsTargetingGeoLocationZipStruct
>;
type AddGoogleAdsTargetingGeoLocationCountry = Infer<
	typeof AddGoogleAdsTargetingGeoLocationCountryStruct
>;

// Google Ads location utilities
export function createGoogleAdsAddressLocation(
	name: string,
	address_string: string,
	radius = 25
): AddGoogleAdsTargetingGeoLocationCustomLocation {
	return {
		name: name || '',
		radius,
		address_string: address_string.trim(),
		distance_unit: 'mile',
		excluded: false,
	};
}

export function createGoogleAdsZipLocation(
	key: string,
	country_code: string
): AddGoogleAdsTargetingGeoLocationZip {
	return {
		key: key.trim(),
		country_code: country_code.trim(),
		excluded: false,
	};
}

export function createGoogleAdsCountryLocation(
	key: string
): AddGoogleAdsTargetingGeoLocationCountry {
	return {
		key: key.trim(),
		excluded: false,
	};
}

// Google Ads previous locations processing
export function processGoogleAdsPreviousLocations(
	previousLocations: ViewGoogleAdsCampaignLocations,
	existingCustomAddresses: Set<string>,
	existingZipKeys: Set<string>,
	existingCountryKeys: Set<string>
): {
	custom: AddGoogleAdsTargetingGeoLocationCustomLocation[];
	zips: AddGoogleAdsTargetingGeoLocationZip[];
	countries: AddGoogleAdsTargetingGeoLocationCountry[];
} {
	const custom: AddGoogleAdsTargetingGeoLocationCustomLocation[] = [];
	const zips: AddGoogleAdsTargetingGeoLocationZip[] = [];
	const countries: AddGoogleAdsTargetingGeoLocationCountry[] = [];

	for (const location of previousLocations.custom_locations ?? []) {
		if (existingCustomAddresses.has(location.address_string)) continue;
		custom.push({
			...location,
			excluded: location.excluded ?? false,
		});
	}

	for (const location of previousLocations.zips ?? []) {
		if (existingZipKeys.has(location.key)) continue;
		zips.push({
			...location,
			excluded: location.excluded ?? false,
		});
	}

	for (const location of previousLocations.countries ?? []) {
		if (existingCountryKeys.has(location.key)) continue;
		countries.push({
			...location,
			excluded: location.excluded ?? false,
		});
	}

	return { custom, zips, countries };
}

// Google Ads Salesforce location processing
export function processGoogleAdsSalesforceLocation(
	location: SalesforceLocationData,
	existingCustomAddresses: Set<string>,
	existingZipKeys: Set<string>
): {
	custom: AddGoogleAdsTargetingGeoLocationCustomLocation[];
	zips: AddGoogleAdsTargetingGeoLocationZip[];
} {
	const custom: AddGoogleAdsTargetingGeoLocationCustomLocation[] = [];
	const zips: AddGoogleAdsTargetingGeoLocationZip[] = [];

	// Handle simple address case
	if ('address' in location) {
		if (!existingCustomAddresses.has(location.address)) {
			custom.push(
				createGoogleAdsAddressLocation('Salesforce', location.address)
			);
		}
		return { custom, zips };
	}

	const { addresses, cities, zipCodes, radiusMiles } = location;

	const allZips =
		zipCodes
			?.split(',')
			.filter(Boolean)
			.map((z) => z.trim()) ?? [];
	const allCities =
		cities
			?.split(';')
			.filter(Boolean)
			.map((c) => c.trim()) ?? [];
	const allAddresses = addresses
		.replaceAll(/[[\]]/gu, '')
		.split(/[;\r\n]+/u)
		.map((a) => a.trim())
		.filter(Boolean);

	let countryCode = 'US';

	// Process zips
	for (const newZip of allZips) {
		const country = determineZipCodeType(newZip);
		if (country === 'Unknown') continue;

		countryCode = country;

		if (!existingZipKeys.has(newZip)) {
			zips.push(createGoogleAdsZipLocation(newZip, country));
		}
	}

	// Process cities
	for (const city of allCities) {
		const cityAddress = `${city}, ${countryCode}`;
		if (!existingCustomAddresses.has(cityAddress)) {
			custom.push(
				createGoogleAdsAddressLocation(
					`${location.name} - City`,
					cityAddress,
					25
				)
			);
		}
	}

	// Process addresses with radius
	if (radiusMiles) {
		for (const newAddress of allAddresses) {
			if (!existingCustomAddresses.has(newAddress)) {
				custom.push(
					createGoogleAdsAddressLocation(location.name, newAddress, radiusMiles)
				);
			}
		}
	}

	return { custom, zips };
}

// Google Ads Facebook audience locations processing
export function processGoogleAdsFacebookAudienceLocations(
	googleLocations: (
		| {
				name: string;
				address_string: string;
				radius: number;
				distance_unit: string;
		  }
		| { key: string; country_code?: string }
	)[],
	existingCustomAddresses: Set<string>,
	existingZipKeys: Set<string>,
	existingCustomLocationsMap: Map<string, boolean>,
	existingZipsMap: Map<string, boolean>,
	existingCountriesMap: Map<string, boolean>,
	previousCustomLocationsMap: Map<string, boolean>,
	previousZipsMap: Map<string, boolean>,
	previousCountriesMap: Map<string, boolean>
): {
	custom: AddGoogleAdsTargetingGeoLocationCustomLocation[];
	zips: AddGoogleAdsTargetingGeoLocationZip[];
	countries: AddGoogleAdsTargetingGeoLocationCountry[];
} {
	const custom: AddGoogleAdsTargetingGeoLocationCustomLocation[] = [];
	const zips: AddGoogleAdsTargetingGeoLocationZip[] = [];
	const countries: AddGoogleAdsTargetingGeoLocationCountry[] = [];

	for (const location of googleLocations) {
		if ('address_string' in location) {
			if (existingCustomAddresses.has(location.address_string)) continue;

			const existingExcluded =
				existingCustomLocationsMap.get(location.address_string) ??
				previousCustomLocationsMap.get(location.address_string) ??
				false;

			custom.push({
				...location,
				excluded: existingExcluded,
			});
		} else if ('key' in location) {
			// Check if this is a country (no country_code means it's a country, not a zip)
			if (location.country_code === undefined) {
				// This is a country
				if (existingCountriesMap.has(location.key)) continue;

				const existingExcluded =
					existingCountriesMap.get(location.key) ??
					previousCountriesMap.get(location.key) ??
					false;

				countries.push({
					key: location.key,
					excluded: existingExcluded,
				});
			} else {
				// This is a zip
				if (existingZipKeys.has(location.key)) continue;

				const existingExcluded =
					existingZipsMap.get(location.key) ??
					previousZipsMap.get(location.key) ??
					false;

				zips.push({
					...location,
					excluded: existingExcluded,
				});
			}
		}
	}

	return { custom, zips, countries };
}
